import { useState } from 'react'
import HoverableCellWrapper from '@/components/hoverable-cell-wrapper/HoverableCellWrapper'
import ReferencedTextItem from './ReferencedTextItem'
import type { MatterAssetEventFieldItem } from '@/matter-asset-common/feature/matter-asset-feature-config'
import type { CellPosition, ScrollToCell } from '@/medical-common/hooks/use-table-cell-scroll'

// Field mode: single item
interface FieldProps {
  item: MatterAssetEventFieldItem
  cellPosition: CellPosition
  scrollToCell: ScrollToCell
}

// List mode: multiple items
interface ListProps {
  items: MatterAssetEventFieldItem[]
  cellPosition: CellPosition
  scrollToCell: ScrollToCell
}
type ReferencedTextCellProps = FieldProps | ListProps

/**
 * Referenced Text Cell
 *
 * Table cell component for displaying referenced text fields in either single or list form.
 * Wraps content with HoverableCellWrapper to enable cell-level hover to highlight the first item in the cell.
 */
export default function ReferencedTextCell(props: ReferencedTextCellProps) {
  const [cellHovered, setCellHovered] = useState(false)

  const isListMode = 'items' in props

  // List mode
  if (isListMode) {
    const { items, cellPosition, scrollToCell } = props

    if (items.length === 0) {
      return <div className="px-4 py-2 text-sm text-brand-neutral-500 italic">None provided</div>
    }

    return (
      <HoverableCellWrapper onCellHover={setCellHovered}>
        <div className="px-4 py-2">
          <ul className="text-sm pl-4 space-y-1">
            {items.map((item, index) => (
              <ReferencedTextItem
                key={index}
                item={item}
                cellPosition={cellPosition}
                scrollToCell={scrollToCell}
                isFirst={index === 0}
                bulleted
                isCellHovered={cellHovered}
              />
            ))}
          </ul>
        </div>
      </HoverableCellWrapper>
    )
  }

  // Single field mode
  const { item, cellPosition, scrollToCell } = props

  return (
    <HoverableCellWrapper onCellHover={setCellHovered}>
      {/* Single-line citation cells are vertically centered within the row (Figma: justify-center) */}
      <div className="h-full flex flex-col justify-center px-4 py-2">
        <ReferencedTextItem item={item} cellPosition={cellPosition} scrollToCell={scrollToCell} bulleted={false} isFirst isCellHovered={cellHovered} />
      </div>
    </HoverableCellWrapper>
  )
}
